import { useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import styles from './PDFViewer.module.css'

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

interface PDFViewerProps {
  pdfUrl: string | null
  zoom: number
  isLoading: boolean
  error: string | null
  onPageCountChange?: (pageCount: number) => void
}

export function PDFViewer({ pdfUrl, zoom, isLoading, error, onPageCountChange }: PDFViewerProps) {
  const [numPages, setNumPages] = useState<number | null>(null)

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages)
    onPageCountChange?.(numPages)
  }

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <span>Compiling LaTeX...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <span className={styles.errorIcon}>!</span>
          <span className={styles.errorTitle}>Compilation Error</span>
          <p className={styles.errorMessage}>{error}</p>
        </div>
      </div>
    )
  }

  if (!pdfUrl) {
    return (
      <div className={styles.container}>
        <div className={styles.placeholder}>
          <span>No PDF to display</span>
          <span className={styles.hint}>Edit the LaTeX code to compile</span>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div
        className={styles.documentWrapper}
        style={{ transform: `scale(${zoom / 100})` }}
      >
        {/* L-shaped shadow with curved taper */}
        <div className={styles.paperShadowSvg}>
          <svg viewBox="0 0 532 680" preserveAspectRatio="none">
            <path
              d="M 12 0 L 12 673 L 532 673 L 532 680 L 7 680 C 7 220, 4 120, 4 0 Z"
              fill="rgba(0, 0, 0, 0.45)"
            />
          </svg>
        </div>
        <Document
          file={pdfUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={
            <div className={styles.loading}>
              <div className={styles.spinner} />
            </div>
          }
          error={
            <div className={styles.error}>
              <span>Failed to load PDF</span>
            </div>
          }
        >
          {Array.from(new Array(numPages || 1), (_, index) => (
            <Page
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              className={styles.page}
              renderTextLayer={true}
              renderAnnotationLayer={true}
            />
          ))}
        </Document>
      </div>
    </div>
  )
}
